import React, {
  createElement,
  type FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import type { SxProps, Theme } from '@mui/material'
import { Box, ButtonBase, IconButton, Popover, Tooltip, Typography, useTheme } from '@mui/material'
import {
  FirstPage,
  LastPage,
  MoreHoriz,
  NavigateBefore,
  NavigateNext,
  South,
} from '@mui/icons-material'
import type {
  ClaudeSession,
  Command,
  ConversationTurn,
  ConversationTurnEntry,
  ErrorRecoveryType,
  UUID,
} from '@/index'
import { isClaudeApiError } from '@/features/events/conversation/models'
import { EventType } from '@/features/events/shared/types'
import { isNotInteractable } from '@/features/events/conversation/state/types'
import { isExitPlanModeToolInput } from '@/features/events/shared/types/tool-inputs'
import { ActivityFeedBar } from '@/features/events/activity'
import {
  ConversationEventStatusIcon,
  ExpandControl,
  expandControlIconButtonSx,
  InjectUserInputControl,
  InterruptAndResumeControl,
  useExpandDialog,
  useCommandSubmitter,
  useUserCommandDialogs,
  useZoomLevel,
  ZoomControls,
} from '../controls'
import { ToastNotification, standardTooltipProps } from '@/common/components'
import { AgentModeBadgeTransition } from '../agent-mode-badges'
import { SessionDialog } from './SessionDialog'
import {
  AgentNarrativeBubble,
  CollectUserQuestionsBubble,
  ErroredEventBubble,
  EventBubble,
  FileModificationBubble,
  NotificationBubble,
  ConversationCommandBubble,
  UserInputBubble,
} from '../bubbles'
import {
  createActorHeaderContainerSx,
  createActorLabelSx,
  createActorLineSx,
} from '../bubbles/styles'
import { UserInputDialog } from './UserInputDialog'
import { EventDetailsDialog } from './EventDetailsDialog'
import { MostRecentEventDialog } from './MostRecentEventDialog'
import {
  Actor,
  type MessageGroup,
  type TimelineItem,
  TimelineItemType,
  useConversationNotifications,
  useConversationTimeline,
} from '../timeline'
import { useDialog } from '@/common/hooks'
import {
  selectPlanApprovalContextRenderer,
  selectToolPermissionContextRenderer,
} from '../utils/contextRendererSelectors'
import {
  deriveAgentModeState as deriveAgentModeStateFromUtils,
  getPreviousConversationTurn as getPreviousConversationTurnFromUtils,
} from '@/features/events/shared/utils/agentModeUtils'

const noop = () => {}

import { ERRORED_TOOL_NAME } from '@/infra/sse/conversation/decryption/resolveNotificationContext'

interface ConversationDialogProps {
  readonly open: boolean
  readonly onClose: () => void
  readonly session: ClaudeSession
  readonly userPromptId: string
  readonly recordCommand: (sessionId: UUID, userPromptId: UUID, userCommand: Command) => void
  readonly createNewTurn: (session: ClaudeSession, userPromptId: UUID, userCommand: Command) => void
}

interface EventBubbleProps {
  readonly event: ConversationTurnEntry
  readonly isAgent: boolean
  readonly session: ClaudeSession
  readonly conversationTurn: ConversationTurn | null
  readonly latestEvent: ConversationTurnEntry | null
  readonly allUserCommands: Command[]
  readonly recordCommand: (sessionId: UUID, userPromptId: UUID, userCommand: Command) => void
  readonly submitErrorRecovery: (
    event: ConversationTurnEntry,
    recoveryType: ErrorRecoveryType,
  ) => Promise<void>
  readonly onOptionSelect?: (optionIndex: number, event: ConversationTurnEntry) => void
  readonly onEventDetailsClick?: (event: ConversationTurnEntry) => void
}

interface TimelineItemBubbleProps {
  readonly item: TimelineItem
  readonly isAgent: boolean
  readonly session: ClaudeSession
  readonly conversationTurn: ConversationTurn | null
  readonly latestEvent: ConversationTurnEntry | null
  readonly allUserCommands: Command[]
  readonly recordCommand: (sessionId: UUID, userPromptId: UUID, userCommand: Command) => void
  readonly submitErrorRecovery: (
    event: ConversationTurnEntry,
    recoveryType: ErrorRecoveryType,
  ) => Promise<void>
  readonly onOptionSelect: (optionIndex: number, event: ConversationTurnEntry) => void
  readonly onEventDetailsClick: (event: ConversationTurnEntry) => void
}

interface MessageGroupProps {
  readonly group: MessageGroup
  readonly isFirst: boolean
  readonly session: ClaudeSession
  readonly conversationTurn: ConversationTurn | null
  readonly latestEvent: ConversationTurnEntry | null
  readonly allUserCommands: Command[]
  readonly recordCommand: (sessionId: UUID, userPromptId: UUID, userCommand: Command) => void
  readonly submitErrorRecovery: (
    event: ConversationTurnEntry,
    recoveryType: ErrorRecoveryType,
  ) => Promise<void>
  readonly onOptionSelect: (optionIndex: number, event: ConversationTurnEntry) => void
  readonly onEventDetailsClick: (event: ConversationTurnEntry) => void
}

interface EventTimelineProps {
  readonly messageGroups: MessageGroup[]
  readonly session: ClaudeSession
  readonly conversationTurn: ConversationTurn | null
  readonly latestEvent: ConversationTurnEntry | null
  readonly allUserCommands: Command[]
  readonly recordCommand: (sessionId: UUID, userPromptId: UUID, userCommand: Command) => void
  readonly submitErrorRecovery: (
    event: ConversationTurnEntry,
    recoveryType: ErrorRecoveryType,
  ) => Promise<void>
  readonly onOptionSelect: (optionIndex: number, event: ConversationTurnEntry) => void
  readonly onEventDetailsClick: (event: ConversationTurnEntry) => void
}

interface UseScrollToBottomReturn {
  readonly showButton: boolean
  readonly scrollToBottom: () => void
  readonly containerRef: React.RefObject<HTMLElement | null>
}

interface TurnNavigationControlsProps {
  readonly currentIndex: number
  readonly totalTurns: number
  readonly hasPrevious: boolean
  readonly hasNext: boolean
  readonly onPrevious: () => void
  readonly onNext: () => void
  readonly sortedTurns: ConversationTurn[]
  readonly currentTurnId: UUID
  readonly onSelectTurn: (turnId: UUID) => void
}

interface UseTurnNavigationReturn {
  readonly currentIndex: number
  readonly totalTurns: number
  readonly hasPrevious: boolean
  readonly hasNext: boolean
  readonly previousTurnId: UUID | null
  readonly nextTurnId: UUID | null
  readonly sortedTurns: ConversationTurn[]
}

export const ConversationDialog: FC<ConversationDialogProps> = ({
  open,
  onClose,
  session,
  userPromptId,
  recordCommand,
  createNewTurn,
}) => {
  const { notification, showError, close: closeNotification } = useConversationNotifications()
  const eventDetailsDialog = useDialog()
  const mostRecentEventDialog = useDialog()
  const [selectedEventForDetails, setSelectedEventForDetails] =
    useState<ConversationTurnEntry | null>(null)
  const [userPromptIdState, setUserPromptIdState] = useState(userPromptId)

  useEffect(() => {
    if (open) {
      setUserPromptIdState(userPromptId)
    }
  }, [open, userPromptId])

  const turnNavigation = useTurnNavigation(session, userPromptIdState)

  const handlePreviousTurn = useCallback(() => {
    if (turnNavigation.previousTurnId) {
      setUserPromptIdState(turnNavigation.previousTurnId)
    }
  }, [turnNavigation.previousTurnId])

  const handleNextTurn = useCallback(() => {
    if (turnNavigation.nextTurnId) {
      setUserPromptIdState(turnNavigation.nextTurnId)
    }
  }, [turnNavigation.nextTurnId])

  const handleSelectTurn = useCallback((turnId: UUID) => {
    setUserPromptIdState(turnId)
  }, [])

  const conversationTurn = useMemo(
    () => session.conversationTurns.get(userPromptIdState),
    [session.conversationTurns, userPromptIdState],
  )

  const previousTurn = useMemo(
    () => getPreviousConversationTurnFromUtils(session, userPromptId),
    [session, userPromptId],
  )

  const agentModeState = useMemo(
    () => deriveAgentModeStateFromUtils(conversationTurn, previousTurn, session.currentAgentMode),
    [conversationTurn, previousTurn, session.currentAgentMode],
  )

  const currentAgentMode = agentModeState.currentMode

  const latestEvent = useMemo(() => {
    if (!conversationTurn || conversationTurn.events.length === 0) return null
    return conversationTurn.events.at(-1) ?? null
  }, [conversationTurn])

  const agentModeBadgeComponent = useMemo(() => {
    if (agentModeState.pendingTransition) {
      return createElement(AgentModeBadgeTransition, {
        from: agentModeState.pendingTransition.from,
        to: agentModeState.pendingTransition.to,
      })
    }
    return undefined
  }, [agentModeState.pendingTransition])

  const submitter = useCommandSubmitter({
    session,
    conversationTurn: conversationTurn ?? null,
    recordCommand,
    createNewTurn,
    onError: (error: Error) => {
      showError(error.message || 'Failed to submit command')
    },
  })

  const dialogs = useUserCommandDialogs({
    session,
    conversationTurn: conversationTurn ?? null,
    submitter,
    currentAgentMode,
  })

  const { messageGroups } = useConversationTimeline(
    conversationTurn?.events || [],
    conversationTurn?.userCommands || [],
  )

  const isAnyDialogOpen = useMemo(
    () => dialogs.inject.isOpen || dialogs.reply.isOpen || dialogs.userOption.isOpen,
    [dialogs.inject.isOpen, dialogs.reply.isOpen, dialogs.userOption.isOpen],
  )

  const { showButton: showScrollButton, scrollToBottom } = useScrollToBottom(open)
  const { isExpanded, handleToggleExpand } = useExpandDialog(open)
  const { zoomLevel, fontSize, handleZoomIn, handleZoomOut, handleZoomReset, showZoomLevel } =
    useZoomLevel(open)

  const windowControlSlot2 = useMemo(() => {
    if (!showScrollButton) return null

    return (
      <Tooltip {...standardTooltipProps} title="Scroll to bottom">
        <IconButton onClick={scrollToBottom} size="small" sx={expandControlIconButtonSx}>
          <South fontSize="small" />
        </IconButton>
      </Tooltip>
    )
  }, [showScrollButton, scrollToBottom])

  const windowControlSlot3 = useMemo(
    () => <ExpandControl isExpanded={isExpanded} onToggle={handleToggleExpand} />,
    [isExpanded, handleToggleExpand],
  )

  const controlsRowLeft = useMemo(
    () => (
      <ZoomControls
        zoomLevel={zoomLevel}
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
        onZoomReset={handleZoomReset}
        showZoomLevel={showZoomLevel}
      />
    ),
    [zoomLevel, handleZoomIn, handleZoomOut, handleZoomReset, showZoomLevel],
  )

  const handleCloseNotification = useCallback(() => {
    closeNotification()
    submitter.clearError()
  }, [closeNotification, submitter])

  const handleOpenEventDetails = useCallback(
    (event: ConversationTurnEntry) => {
      setSelectedEventForDetails(event)
      eventDetailsDialog.open()
    },
    [eventDetailsDialog],
  )

  useEffect(() => {
    if (!open) return

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft' && event.shiftKey && turnNavigation.hasPrevious) {
        event.preventDefault()
        handlePreviousTurn()
      } else if (event.key === 'ArrowRight' && event.shiftKey && turnNavigation.hasNext) {
        event.preventDefault()
        handleNextTurn()
      }
    }

    globalThis.addEventListener('keydown', handleKeyDown)
    return () => globalThis.removeEventListener('keydown', handleKeyDown)
  }, [open, turnNavigation.hasPrevious, turnNavigation.hasNext, handlePreviousTurn, handleNextTurn])

  const previousTurnIdRef = useRef<UUID | null>(null)

  useEffect(() => {
    if (!open) return

    if (previousTurnIdRef.current !== null && previousTurnIdRef.current !== userPromptIdState) {
      const dialogContent = document.querySelector('[role="dialog"] .MuiDialogContent-root')
      if (dialogContent) {
        dialogContent.scrollTop = 0
      }
    }

    previousTurnIdRef.current = userPromptIdState
  }, [open, userPromptIdState])

  const isCurrentTurn = userPromptIdState === userPromptId
  const isSessionInteractable = !isNotInteractable(session)
  const shouldShowActionControls = isCurrentTurn && isSessionInteractable

  const centerControls = useMemo(() => {
    if (!isCurrentTurn) return null

    return (
      <Box sx={{ display: 'flex', gap: 1.3, alignItems: 'center' }}>
        {shouldShowActionControls && (
          <InterruptAndResumeControl
            conversationId={userPromptId}
            sessionId={session.sessionId}
            conversationTurn={conversationTurn}
            onInterrupt={dialogs.handlePause}
            onResume={dialogs.handleResume}
          />
        )}
        <ConversationEventStatusIcon
          conversationTurn={conversationTurn}
          isProcessing={dialogs.controls.isProcessing}
          isPaused={dialogs.controls.isPaused}
          isInteractable={isSessionInteractable}
          onReply={isSessionInteractable ? dialogs.reply.open : noop}
          onViewMostRecent={mostRecentEventDialog.open}
          userActivity={session.runtime.userActivity}
        />
        {shouldShowActionControls && (
          <InjectUserInputControl
            conversationId={userPromptId}
            sessionId={session.sessionId}
            conversationTurn={conversationTurn}
            onInject={dialogs.inject.open}
          />
        )}
      </Box>
    )
  }, [
    isCurrentTurn,
    shouldShowActionControls,
    isSessionInteractable,
    session,
    conversationTurn,
    userPromptId,
    dialogs.handlePause,
    dialogs.handleResume,
    dialogs.inject.open,
    dialogs.reply.open,
    dialogs.controls.isProcessing,
    dialogs.controls.isPaused,
    mostRecentEventDialog.open,
  ])

  const statusRowContent = useMemo(() => {
    return (
      <ActivityFeedBar
        sessionId={session.sessionId}
        latestActivity={session.runtime.latestActivity}
      />
    )
  }, [session])

  const rightControls = useMemo(() => {
    if (turnNavigation.totalTurns <= 1) return null

    return (
      <TurnNavigationControls
        currentIndex={turnNavigation.currentIndex}
        totalTurns={turnNavigation.totalTurns}
        hasPrevious={turnNavigation.hasPrevious}
        hasNext={turnNavigation.hasNext}
        onPrevious={handlePreviousTurn}
        onNext={handleNextTurn}
        sortedTurns={turnNavigation.sortedTurns}
        currentTurnId={userPromptIdState}
        onSelectTurn={handleSelectTurn}
      />
    )
  }, [
    turnNavigation.totalTurns,
    turnNavigation.currentIndex,
    turnNavigation.hasPrevious,
    turnNavigation.hasNext,
    turnNavigation.sortedTurns,
    userPromptIdState,
    handlePreviousTurn,
    handleNextTurn,
    handleSelectTurn,
  ])

  const conversationContent = renderConversationContent({
    conversationTurn: conversationTurn ?? null,
    isAnyDialogOpen,
    messageGroups,
    session,
    latestEvent,
    recordCommand,
    submitErrorRecovery: submitter.submitErrorRecovery,
    onOptionSelect: dialogs.userOption.handleSelect,
    onEventDetailsClick: handleOpenEventDetails,
  })

  return (
    <>
      <SessionDialog
        open={open}
        onClose={onClose}
        title="Conversation"
        breadcrumb={session.sessionBreadcrumb}
        agentMode={currentAgentMode}
        agentModeBadge={agentModeBadgeComponent}
        dangerouslySkipPermissions={session.dangerouslySkipPermissions}
        autoModeSupported={session.autoModeSupported}
        windowControlSlot2={windowControlSlot2}
        windowControlSlot3={windowControlSlot3}
        controlsRowLeft={controlsRowLeft}
        controlsRowCenter={centerControls}
        controlsRowRight={rightControls}
        statusRowCenter={statusRowContent}
        isExpanded={isExpanded}
        fontSize={fontSize}
      >
        {conversationContent}
      </SessionDialog>

      <ToastNotification
        open={notification.open}
        message={notification.message}
        severity={notification.severity}
        onClose={handleCloseNotification}
        autoHideDuration={notification.severity === 'error' ? 6000 : 4000}
      />

      <UserInputDialog
        open={dialogs.inject.isOpen}
        onClose={dialogs.inject.close}
        onSubmit={dialogs.inject.handleSubmit}
        session={session}
        isSubmitting={submitter.isSubmitting}
        error={submitter.error}
        draftType="injected_prompt"
        refEventId={null}
        title="Inject Prompt"
        placeholder="Enter additional instructions or context for Claude Code..."
        description={`This will inject your prompt into the current conversation without interrupting Claude Code's execution.
Your draft is saved when you close this dialog and restored when you reopen it.
Each conversation maintains its own draft.
Drafts are cleared only after successful submission.`}
        currentAgentMode={currentAgentMode}
        dangerouslySkipPermissions={session.dangerouslySkipPermissions}
        autoModeSupported={session.autoModeSupported}
        submitButtonLabel="Inject"
        submitButtonLoadingLabel="Injecting..."
      />

      <UserInputDialog
        open={dialogs.reply.isOpen}
        onClose={dialogs.reply.close}
        onSubmit={dialogs.reply.handleSubmit}
        session={session}
        isSubmitting={submitter.isSubmitting}
        error={submitter.error}
        draftType="user_reply"
        refEventId={userPromptId}
        title="Reply to Agent"
        placeholder="Enter your reply to Claude Code..."
        description={`Reply to Claude Code's request for input.
Your draft is saved when you close this dialog and restored when you reopen it.
Each conversation maintains its own draft.
Drafts are cleared only after successful submission.`}
        currentAgentMode={currentAgentMode}
        dangerouslySkipPermissions={session.dangerouslySkipPermissions}
        autoModeSupported={session.autoModeSupported}
        submitButtonLabel="Reply"
        submitButtonLoadingLabel="Replying..."
      />

      {dialogs.userOption.selectedOption && dialogs.userOption.selectedEvent && (
        <UserInputDialog
          open={dialogs.userOption.isOpen}
          onClose={dialogs.userOption.close}
          onSubmit={dialogs.userOption.handleSubmit}
          session={session}
          isSubmitting={submitter.isSubmitting}
          error={submitter.error}
          draftType="custom_option"
          refEventId={dialogs.userOption.selectedEvent.eventId}
          title="Custom Response"
          placeholder="Explain what you want Claude Code to do differently..."
          description={dialogs.userOption.selectedOption.option}
          currentAgentMode={currentAgentMode}
          dangerouslySkipPermissions={session.dangerouslySkipPermissions}
          autoModeSupported={session.autoModeSupported}
        />
      )}

      {selectedEventForDetails && (
        <EventDetailsDialog
          open={eventDetailsDialog.isOpen}
          onClose={eventDetailsDialog.close}
          event={selectedEventForDetails}
          session={session}
        />
      )}

      {latestEvent && (
        <MostRecentEventDialog
          open={mostRecentEventDialog.isOpen}
          onClose={mostRecentEventDialog.close}
          event={latestEvent}
          session={session}
          latestEvent={latestEvent}
          conversationTurn={conversationTurn ?? null}
          recordCommand={recordCommand}
          createNewTurn={createNewTurn}
          onOpenConversation={mostRecentEventDialog.close}
        />
      )}
    </>
  )
}

const TurnNavigationControls: FC<TurnNavigationControlsProps> = ({
  currentIndex,
  hasPrevious,
  hasNext,
  onPrevious,
  onNext,
  sortedTurns,
  currentTurnId,
  onSelectTurn,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)

  const handleOpenMenu = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }, [])

  const handleCloseMenu = useCallback(() => {
    setAnchorEl(null)
  }, [])

  const handleSelectTurn = useCallback(
    (turnId: UUID) => {
      onSelectTurn(turnId)
      handleCloseMenu()
    },
    [onSelectTurn, handleCloseMenu],
  )

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Tooltip {...standardTooltipProps} title="First turn">
        <span>
          <IconButton
            onClick={() => sortedTurns[0] && onSelectTurn(sortedTurns[0].userPromptId)}
            size="small"
            disabled={currentIndex === 0}
            sx={expandControlIconButtonSx}
          >
            <FirstPage fontSize="medium" />
          </IconButton>
        </span>
      </Tooltip>

      <Tooltip {...standardTooltipProps} title="Previous turn">
        <span>
          <IconButton
            onClick={onPrevious}
            size="small"
            disabled={!hasPrevious}
            sx={expandControlIconButtonSx}
          >
            <NavigateBefore fontSize="medium" />
          </IconButton>
        </span>
      </Tooltip>

      <Tooltip {...standardTooltipProps} title="View all turns">
        <IconButton onClick={handleOpenMenu} size="small" sx={expandControlIconButtonSx}>
          <MoreHoriz fontSize="medium" />
        </IconButton>
      </Tooltip>

      <Tooltip {...standardTooltipProps} title="Next turn">
        <span>
          <IconButton
            onClick={onNext}
            size="small"
            disabled={!hasNext}
            sx={expandControlIconButtonSx}
          >
            <NavigateNext fontSize="medium" />
          </IconButton>
        </span>
      </Tooltip>

      <Tooltip {...standardTooltipProps} title="Last turn">
        <span>
          <IconButton
            onClick={() => {
              const lastTurn = sortedTurns.at(-1)
              if (lastTurn) onSelectTurn(lastTurn.userPromptId)
            }}
            size="small"
            disabled={currentIndex === sortedTurns.length - 1}
            sx={expandControlIconButtonSx}
          >
            <LastPage fontSize="medium" />
          </IconButton>
        </span>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleCloseMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Box sx={turnGridContainerSx}>
          {sortedTurns.map((turn, index) => (
            <ButtonBase
              key={turn.userPromptId}
              onClick={() => handleSelectTurn(turn.userPromptId)}
              sx={turnGridItemSx(turn.userPromptId === currentTurnId)}
            >
              {index + 1}
            </ButtonBase>
          ))}
        </Box>
      </Popover>
    </Box>
  )
}

function useScrollToBottom(open: boolean): UseScrollToBottomReturn {
  const [showButton, setShowButton] = useState(false)
  const containerRef = useRef<HTMLElement | null>(null)

  const checkScrollPosition = useCallback(() => {
    if (!containerRef.current) return

    const { scrollTop, scrollHeight, clientHeight } = containerRef.current
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight

    setShowButton(distanceFromBottom > SCROLL_THRESHOLD)
  }, [])

  const scrollToBottom = useCallback(() => {
    if (!containerRef.current) return

    containerRef.current.scrollTo({
      top: containerRef.current.scrollHeight,
      behavior: 'smooth',
    })
  }, [])

  useEffect(() => {
    if (!open) {
      setShowButton(false)
      return
    }

    const timeoutId = setTimeout(() => {
      const dialogContents = document.querySelectorAll('.MuiDialogContent-root')
      if (dialogContents.length === 0) return

      const dialogContent = Array.from(dialogContents).at(-1) as HTMLElement
      containerRef.current = dialogContent

      checkScrollPosition()

      dialogContent.addEventListener('scroll', checkScrollPosition)
    }, 100)

    return () => {
      clearTimeout(timeoutId)
      if (containerRef.current) {
        containerRef.current.removeEventListener('scroll', checkScrollPosition)
      }
    }
  }, [open, checkScrollPosition])

  return { showButton, scrollToBottom, containerRef }
}

interface CommonBubbleProps {
  readonly event: ConversationTurnEntry
  readonly session: ClaudeSession
  readonly latestEvent: ConversationTurnEntry | null
  readonly allUserCommands: Command[]
  readonly onOptionSelect?: (optionIndex: number, event: ConversationTurnEntry) => void
  readonly onEventDetailsClick?: (event: ConversationTurnEntry) => void
}

function renderPlanApprovalBubble(
  event: ConversationTurnEntry,
  commonProps: CommonBubbleProps,
): React.ReactNode | null {
  const isPlanApproval =
    event.eventType === EventType.PLAN_APPROVAL_REQUEST &&
    event.notificationContext?.toolInput &&
    isExitPlanModeToolInput(event.notificationContext.toolInput)

  if (!isPlanApproval) return null

  const contextRenderer = selectPlanApprovalContextRenderer(event.notificationContext.toolInput)
  return <NotificationBubble {...commonProps}>{contextRenderer}</NotificationBubble>
}

function renderToolPermissionBubble(
  event: ConversationTurnEntry,
  commonProps: CommonBubbleProps,
  session: ClaudeSession,
): React.ReactNode | null {
  if (event.eventType !== EventType.TOOL_PERMISSION_REQUEST || !event.notificationContext) {
    return null
  }

  const { toolName, toolInput } = event.notificationContext

  if (
    isNotInteractable(session) &&
    toolName === ERRORED_TOOL_NAME &&
    event.notificationMessage?.trim()
  ) {
    return (
      <NotificationBubble {...commonProps}>
        <Typography>{event.notificationMessage}</Typography>
      </NotificationBubble>
    )
  }

  const contextRenderer = selectToolPermissionContextRenderer(toolName, toolInput)
  return <NotificationBubble {...commonProps}>{contextRenderer}</NotificationBubble>
}

function isErroredEvent(event: ConversationTurnEntry): boolean {
  if (event.eventType === EventType.ERRORED_EVENT) return true
  return event.eventType === EventType.AGENT_REPLY && isClaudeApiError(event.agentResponse)
}

const EventBubbleRouter: FC<EventBubbleProps> = ({
  event,
  isAgent,
  session,
  conversationTurn,
  latestEvent,
  allUserCommands,
  recordCommand,
  submitErrorRecovery,
  onOptionSelect,
  onEventDetailsClick,
}) => {
  const commonProps: CommonBubbleProps = {
    event,
    session,
    latestEvent,
    allUserCommands,
    ...(onOptionSelect ? { onOptionSelect } : {}),
    ...(onEventDetailsClick ? { onEventDetailsClick } : {}),
  }

  if (event.eventType === EventType.FILE_MODIFICATION) {
    return (
      <FileModificationBubble event={event} {...(onEventDetailsClick && { onEventDetailsClick })} />
    )
  }

  if (event.eventType === EventType.AGENT_NARRATIVE) {
    return (
      <AgentNarrativeBubble event={event} {...(onEventDetailsClick && { onEventDetailsClick })} />
    )
  }

  const planApprovalBubble = renderPlanApprovalBubble(event, commonProps)
  if (planApprovalBubble) return planApprovalBubble

  const toolPermissionBubble = renderToolPermissionBubble(event, commonProps, session)
  if (toolPermissionBubble) return toolPermissionBubble

  if (event.eventType === EventType.AGENT_QUESTIONS) {
    return (
      <CollectUserQuestionsBubble
        event={event}
        session={session}
        conversationTurn={conversationTurn}
        latestEvent={latestEvent}
        allUserCommands={allUserCommands}
        recordCommand={recordCommand}
        {...(onEventDetailsClick && { onEventDetailsClick })}
      />
    )
  }

  if (event.eventType === EventType.NON_INTERACTIVE_NOTIFICATION) {
    return (
      <NotificationBubble {...commonProps}>
        <Typography>{event.notificationMessage}</Typography>
      </NotificationBubble>
    )
  }

  if (isErroredEvent(event)) {
    return (
      <ErroredEventBubble
        event={event}
        session={session}
        latestEvent={latestEvent}
        allUserCommands={allUserCommands}
        onOptionSelect={onOptionSelect}
        onEventDetailsClick={onEventDetailsClick}
        submitErrorRecovery={submitErrorRecovery}
      />
    )
  }

  if (event.eventType === EventType.USER_PROMPT) {
    return <UserInputBubble event={event} />
  }

  return <EventBubble {...commonProps} isAgent={isAgent} />
}

const TimelineItemBubble: FC<TimelineItemBubbleProps> = ({
  item,
  isAgent,
  session,
  conversationTurn,
  latestEvent,
  allUserCommands,
  recordCommand,
  submitErrorRecovery,
  onOptionSelect,
  onEventDetailsClick,
}) => {
  if (item.type === TimelineItemType.AGENT_EVENT) {
    return (
      <EventBubbleRouter
        event={item.data}
        isAgent={isAgent}
        session={session}
        conversationTurn={conversationTurn}
        latestEvent={latestEvent}
        allUserCommands={allUserCommands}
        recordCommand={recordCommand}
        submitErrorRecovery={submitErrorRecovery}
        onOptionSelect={onOptionSelect}
        onEventDetailsClick={onEventDetailsClick}
      />
    )
  }
  return <ConversationCommandBubble command={item.data} />
}

const MessageGroupComponent: FC<MessageGroupProps> = ({
  group,
  isFirst,
  session,
  conversationTurn,
  latestEvent,
  allUserCommands,
  recordCommand,
  submitErrorRecovery,
  onOptionSelect,
  onEventDetailsClick,
}) => {
  const theme = useTheme()
  const isAgent = group.actor === Actor.AGENT

  return (
    <Box sx={messageGroupContainerSx(isAgent)}>
      <Box sx={createActorHeaderContainerSx(isAgent, isFirst)}>
        <Typography sx={createActorLabelSx(theme)}>{isAgent ? 'Claude' : 'User'}</Typography>
        <Box sx={createActorLineSx(theme)} />
      </Box>

      {group.items.map((item) => (
        <TimelineItemBubble
          key={
            item.type === TimelineItemType.AGENT_EVENT ? item.data.eventId : item.data.userCommandId
          }
          item={item}
          isAgent={isAgent}
          session={session}
          conversationTurn={conversationTurn}
          latestEvent={latestEvent}
          allUserCommands={allUserCommands}
          recordCommand={recordCommand}
          submitErrorRecovery={submitErrorRecovery}
          onOptionSelect={onOptionSelect}
          onEventDetailsClick={onEventDetailsClick}
        />
      ))}
    </Box>
  )
}

const EventTimeline: FC<EventTimelineProps> = ({
  messageGroups,
  session,
  conversationTurn,
  latestEvent,
  allUserCommands,
  recordCommand,
  submitErrorRecovery,
  onOptionSelect,
  onEventDetailsClick,
}) => {
  if (messageGroups.length === 0) {
    return (
      <Box sx={noEventsContainerSx}>
        <Typography variant="body2" color="text.secondary" sx={noEventsTextSx}>
          No events yet
        </Typography>
      </Box>
    )
  }

  return (
    <Box sx={timelineScrollContainerSx}>
      {messageGroups.map((group, index) => {
        const firstItem = group.items[0]

        let key: string
        if (!firstItem) {
          key = `group-${Math.random()}`
        } else if (firstItem.type === TimelineItemType.AGENT_EVENT) {
          key = `group-${firstItem.data.eventId}`
        } else {
          key = `group-${firstItem.data.userCommandId}`
        }

        return (
          <MessageGroupComponent
            key={key}
            group={group}
            isFirst={index === 0}
            session={session}
            conversationTurn={conversationTurn}
            latestEvent={latestEvent}
            allUserCommands={allUserCommands}
            recordCommand={recordCommand}
            submitErrorRecovery={submitErrorRecovery}
            onOptionSelect={onOptionSelect}
            onEventDetailsClick={onEventDetailsClick}
          />
        )
      })}
    </Box>
  )
}

interface RenderConversationContentOptions {
  readonly conversationTurn: ConversationTurn | null
  readonly isAnyDialogOpen: boolean
  readonly messageGroups: MessageGroup[]
  readonly session: ClaudeSession
  readonly latestEvent: ConversationTurnEntry | null
  readonly recordCommand: (sessionId: UUID, userPromptId: UUID, userCommand: Command) => void
  readonly submitErrorRecovery: (
    event: ConversationTurnEntry,
    recoveryType: ErrorRecoveryType,
  ) => Promise<void>
  readonly onOptionSelect: (optionIndex: number, event: ConversationTurnEntry) => void
  readonly onEventDetailsClick: (event: ConversationTurnEntry) => void
}

const noConversationDataContainerSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '200px',
}

const noConversationDataTextSx: SxProps<Theme> = {
  fontStyle: 'italic',
}

const inputModeContainerSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '200px',
}

const inputModeTextSx: SxProps<Theme> = {
  fontStyle: 'italic',
}

const messageGroupContainerSx = (isAgent: boolean): SxProps<Theme> => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: isAgent ? 'flex-start' : 'flex-end',
  mb: 3,
  width: '100%',
})

const noEventsContainerSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '200px',
}

const noEventsTextSx: SxProps<Theme> = {
  fontStyle: 'italic',
}

const timelineScrollContainerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
}

const SCROLL_THRESHOLD = 100

function renderConversationContent(options: RenderConversationContentOptions): React.ReactNode {
  const {
    conversationTurn,
    isAnyDialogOpen,
    messageGroups,
    session,
    latestEvent,
    recordCommand,
    submitErrorRecovery,
    onOptionSelect,
    onEventDetailsClick,
  } = options

  if (!conversationTurn) {
    return (
      <Box sx={noConversationDataContainerSx}>
        <Typography variant="body2" color="text.secondary" sx={noConversationDataTextSx}>
          No conversation data available
        </Typography>
      </Box>
    )
  }

  if (isAnyDialogOpen) {
    return (
      <Box sx={inputModeContainerSx}>
        <Typography variant="body2" color="text.secondary" sx={inputModeTextSx}>
          Conversation timeline hidden while input dialog is open
        </Typography>
      </Box>
    )
  }

  return (
    <EventTimeline
      messageGroups={messageGroups}
      session={session}
      conversationTurn={conversationTurn}
      latestEvent={latestEvent}
      allUserCommands={conversationTurn.userCommands}
      recordCommand={recordCommand}
      submitErrorRecovery={submitErrorRecovery}
      onOptionSelect={onOptionSelect}
      onEventDetailsClick={onEventDetailsClick}
    />
  )
}

function useTurnNavigation(
  session: ClaudeSession,
  currentUserPromptId: UUID,
): UseTurnNavigationReturn {
  const sortedTurns = useMemo(
    () =>
      Array.from(session.conversationTurns.values()).sort(
        (a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime(),
      ),
    [session.conversationTurns],
  )

  const currentIndex = useMemo(
    () => sortedTurns.findIndex((turn) => turn.userPromptId === currentUserPromptId),
    [sortedTurns, currentUserPromptId],
  )

  const totalTurns = sortedTurns.length
  const hasPrevious = currentIndex > 0
  const hasNext = currentIndex < totalTurns - 1

  const previousTurnId = hasPrevious ? (sortedTurns[currentIndex - 1]?.userPromptId ?? null) : null
  const nextTurnId = hasNext ? (sortedTurns[currentIndex + 1]?.userPromptId ?? null) : null

  return {
    currentIndex,
    totalTurns,
    hasPrevious,
    hasNext,
    previousTurnId,
    nextTurnId,
    sortedTurns,
  }
}

const turnGridContainerSx: SxProps<Theme> = {
  display: 'grid',
  gridTemplateColumns: 'repeat(10, 1fr)',
  gap: 0.5,
  p: 1,
}

const turnGridItemSx = (isSelected: boolean): SxProps<Theme> => ({
  width: 28,
  height: 28,
  borderRadius: 1,
  fontSize: '0.7rem',
  fontWeight: isSelected ? 700 : 400,
  color: isSelected ? 'primary.contrastText' : 'text.primary',
  backgroundColor: isSelected ? 'primary.main' : 'transparent',
  '&:hover': {
    backgroundColor: isSelected ? 'primary.dark' : 'action.hover',
  },
})
